/**
 * Persists invoice-hire items as separate invoice-hire-item records
 * (the invoice-hire collection stores them via the invoiceHireItems relation).
 */

import { invoiceHireItemsService } from '../../services';
import { InvoiceHireItem } from '../../types';

const unwrap = (response: any) => response?.data ?? response;

const toItemPayload = (item: InvoiceHireItem, index: number, fallbackDate: string) => ({
  itemNumber: index + 1,
  description: item.description || '-',
  fromDate: item.fromDate || fallbackDate,
  toDate: item.toDate || item.fromDate || fallbackDate,
  unit: item.unit || 'Adjustment',
  quantity: Number(item.quantity) || 1,
  rateUSD: Number(item.rateUSD) || Number(item.amountUSD) || 0,
  amountUSD: Number(item.amountUSD) || 0,
  currency: item.currency || null,
});

/**
 * Creates/updates item records and returns their documentIds
 * so they can be linked on the invoice-hire via invoiceHireItems.
 */
export async function saveInvoiceHireItems(
  items: InvoiceHireItem[],
  fallbackDate: string
): Promise<string[]> {
  const documentIds: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const payload = toItemPayload(item, i, fallbackDate);
    if (item.documentId) {
      await invoiceHireItemsService.update(item.documentId as any, { ...payload, documentId: item.documentId });
      documentIds.push(item.documentId);
    } else {
      const created = unwrap(await invoiceHireItemsService.create(payload));
      if (created?.documentId) documentIds.push(created.documentId);
    }
  }
  return documentIds;
}

/** Deletes item records that were removed from the form during an edit */
export async function deleteRemovedItems(
  previousItems: InvoiceHireItem[] | undefined,
  keptDocumentIds: string[]
): Promise<void> {
  const removed = (previousItems || [])
    .filter((item) => item.documentId && !keptDocumentIds.includes(item.documentId))
    .map((item) => item.documentId as string);
  for (const documentId of removed) {
    try {
      await invoiceHireItemsService.delete(documentId);
    } catch (error) {
      console.error('Failed to delete removed invoice-hire item:', documentId, error);
    }
  }
}

/** Loads the persisted items of an invoice-hire for the edit form */
export async function fetchInvoiceHireItems(invoiceHireDocumentId: string): Promise<InvoiceHireItem[]> {
  try {
    const response: any = await invoiceHireItemsService.getAll({
      filters: { invoiceHire: { documentId: { $eq: invoiceHireDocumentId } } },
      sort: 'itemNumber:ASC',
      pagination: { pageSize: 200 },
    });
    const rows = response?.data || [];
    return rows.map((row: any) => ({
      documentId: row.documentId,
      id: String(row.id ?? row.documentId),
      itemNumber: row.itemNumber,
      description: row.description || '',
      fromDate: row.fromDate || '',
      toDate: row.toDate || '',
      unit: row.unit || '',
      quantity: Number(row.quantity) || 0,
      rateUSD: Number(row.rateUSD) || 0,
      amountUSD: Number(row.amountUSD) || 0,
      currency: row.currency || '',
    }));
  } catch (error) {
    console.error('Failed to load invoice-hire items:', error);
    return [];
  }
}
